import { Button } from '@/components/ui';
import { PlayIcon, PauseIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

interface VideoPlaybackControlsProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
  currentFrame: number;
  onFrameChange: (frame: number) => void;
  totalFrames: number;
  fps: number;
  isVideoReady: boolean;
  className?: string;
  currentPhase?: {
    name: string;
  } | null;
}

export function VideoPlaybackControls({
  isPlaying,
  onTogglePlay,
  currentFrame,
  onFrameChange,
  totalFrames,
  fps: _fps,
  isVideoReady,
  className,
  currentPhase,
}: VideoPlaybackControlsProps) {
  // Frame navigation handlers
  const handlePrevFrame = () => {
    const newFrame = Math.max(currentFrame - 1, 0);
    onFrameChange(newFrame);
  };

  const handleNextFrame = () => {
    const newFrame = Math.min(currentFrame + 1, totalFrames - 1);
    onFrameChange(newFrame);
  };

  return (
    <div className={cn('flex items-center gap-3 p-3 bg-dark-elevated-1 rounded-lg border border-border-subtle', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={onTogglePlay}
        disabled={!isVideoReady}
        className="flex items-center gap-1"
      >
        {isPlaying ? <><PauseIcon className="h-4 w-4" />Pause</> : <><PlayIcon className="h-4 w-4" />Play</>}
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={handlePrevFrame}
        disabled={!isVideoReady || currentFrame === 0}
        className="flex items-center gap-1"
      >
        <ChevronLeftIcon className="h-4 w-4" />Prev
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={handleNextFrame}
        disabled={!isVideoReady || currentFrame >= totalFrames - 1}
        className="flex items-center gap-1"
      >
        Next<ChevronRightIcon className="h-4 w-4" />
      </Button>

      <div className="flex-1 text-center">
        <span className="text-sm font-medium text-text-primary">
          Frame {currentFrame} / {totalFrames - 1}
        </span>
      </div>

      {currentPhase && (
        <div className="text-xs text-accent bg-accent/10 px-2 py-1 rounded">
          Phase: {currentPhase.name}
        </div>
      )}
    </div>
  );
}
